import { inject, Injectable } from '@angular/core';
import { Parcel, Sector, SectorName } from 'libs/domain/sector/sector';
import { ENVIRONMENT, Environment } from 'libs/core/auth/auth.tokens';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FiltersPage } from '../models/sector.interfaces';
import { DataList, PaginatedResult, Sensor } from 'libs/domain';

/**
 * Servei per obtenir dades del backend
 */
@Injectable({
  providedIn: 'root'
})
export class SectorService {

  private http = inject(HttpClient);
  private options: Environment = inject(ENVIRONMENT);
  private baseUrl;
  

  constructor() {
    this.baseUrl = `${this.options.baseUrl.data}`;
  }

  sectors: Sector[] = [];

  loadSectors(
    tenantId: string,
    page: number,
    size: number,
    filters?: any
  ): Observable<PaginatedResult<Sector>> {
    return this.getSectors(tenantId, page, size, filters);
  }

  getSectors(
    tenantId: string,
    page: number,
    size: number,
    filters?: FiltersPage
  ): Observable<PaginatedResult<Sector>> {

    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (filters) {
      if (filters.users_ids?.length)
        params = params.set('id', filters.users_ids.join(','));
      if (filters.sort_by != undefined)
        params = params.set('sort_by', filters.sort_by);
      if (filters.sort_direction != undefined)
        params = params.set('sort_direction', filters.sort_direction);
      if (filters.name != undefined)
        params = params.set('name', filters.name);
      if (filters.states?.length)
        params = params.set('states', filters.states.join(','));
      if (filters.modes?.length)
        params = params.set('modes', filters.modes.join(','));
      if (filters.types?.length)
        params = params.set('types', filters.types.join(','));
      if (filters.parent_id != undefined )
        params = params.set('parent_id', filters.parent_id);
    }

    return this.http.get<PaginatedResult<Sector>>(
      `${this.baseUrl}/communities/${tenantId}/sectors`,
      { params }
    );
  }

  getSector(tenantId: string, sectorId: number): Observable<Sector> {
    return this.http.get<Sector>(
      `${this.baseUrl}/communities/${tenantId}/sectors/${sectorId}`    );
  }

  getSectorSensors(
    tenantId: string,
    sectorId: number
  ): Observable<PaginatedResult<Sensor>> {
    const body = {
      page: 1,
      size: 1, // Només el 1r
      groups: [
        {
          id: sectorId,
          type: 'SECTOR'
        }
      ]
    };

    return this.http.post<PaginatedResult<Sensor>>(`${this.baseUrl}/communities/${tenantId}/sensors`, body);
  }

  getSectorParcels(tenantId: string, sectorId: number) {
    return this.http.get<DataList<Parcel>>(
      `${this.baseUrl}/communities/${tenantId}/sectors/${sectorId}/parcels`);
  }

getSectorsFromIrrigationOutlet(tenantId: string, parentId: number, filters?: FiltersPage) {
    let params = new HttpParams()
      .set('page', 0)
      .set('size', 10)
      .set('parent_id', parentId);

    if (filters) {
      if (filters.users_ids?.length)
        params = params.set('id', filters.users_ids.join(','));
      if (filters.sort_by != undefined)
        params = params.set('sort_by', filters.sort_by);
      if (filters.sort_direction != undefined)
        params = params.set('sort_direction', filters.sort_direction);
      if (filters.name != undefined)
        params = params.set('name', filters.name);
      if (filters.states?.length)
        params = params.set('states', filters.states.join(','));
      if (filters.types?.length)
        params = params.set('types', filters.types.join(','));
      if (filters.parent_id != undefined)
        params = params.set('parent_id', filters.parent_id);
    }

    return this.http.get<PaginatedResult<Sector>>(
      `${this.baseUrl}/communities/${tenantId}/sectors`,
      { params }
    );
  }

  getSectorsNames(tenantId: string): Observable<DataList<SectorName>> {
    return this.http.get<DataList<SectorName>>(`${this.baseUrl}/communities/${tenantId}/sectors-names`);
  }

  // Necessitem aquest altre mètode ja que en la previsió de reg només s'han de poder sel·leccionar tomes
  getIrrigationOutletsNames(
    tenantId: string
  ): Observable<DataList<SectorName>> {
    let params = new HttpParams().set('irrigationOutlets', true);
    return this.http.get<DataList<SectorName>>(
      `${this.baseUrl}/communities/${tenantId}/sectors-names`,
      { params }
    );
  }

  sendManualAction(
    tenantId: string,
    sectorId: number,
    action: 'MANUAL_START' | 'MANUAL_STOP' | 'AUTO'
  ): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/communities/${tenantId}/sectors/${sectorId}/manual-actions`,
      { action }
    );
  }

  updateSectorDescription(
    tenantId: string,
    sectorId: number,
    description: string
  ): Observable<Sector> {
    return this.http.post<Sector>(
      `${this.baseUrl}/communities/${tenantId}/sectors/${sectorId}/description`,
      { description }
    );
  }
}
